import {Icon} from '@/app/components/Icon';
import {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {InputGroup} from '@/components/app/input-group';
import {InputAdornment} from '@/components/app/input-group';
import {CheckList} from '../../../app/components/CheckList';
import {Form} from '../../../app/components/Form';
import {
  useChangePasswordMutation,
  useResetPasswordMutation,
  useResetPasswordResendOTPMutation,
} from '../../../app/services/identitiesApi';
import {emailRegExp, getErrorMessage} from '../../../app/utils/miscUtils';
import {AuthLayout} from '../components/AuthLayout';
import {OTPForm} from '../components/OTPForm';
import {
  containsNumericMessage,
  containsNumericRegex,
  containsSpecialCharacterMessage,
  containsSpecialCharacterRegex,
  minLengthMessage,
  minLengthRegex,
  validatePassword,
} from '../validatePassword';
import {InfoSection} from '../../../app/components/InfoSection';
enum Step {
  Username = 'username',
  OTP = 'otp',
  Password = 'password',
  Completed = 'completed',
}
export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [changePassword] = useChangePasswordMutation();
  const [resetPassword] = useResetPasswordMutation();
  const [resetPasswordResendOTP] = useResetPasswordResendOTPMutation();
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [otp, setOTP] = useState('');
  const [step, setStep] = useState<Step>(() =>
    usernameOrEmail === '' ? Step.Username : ((searchParams.get('step') as Step | null) ?? Step.Username),
  );
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordFocused, setNewPasswordFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setSearchParams({
      step,
    });
    setNewPassword('');
    setError(null);
  }, [step]);
  return (
    <AuthLayout
      backLabel={step === Step.Completed ? undefined : 'Back to Login'}
      backTo={step === Step.Completed ? undefined : '/sign-in'}
      renderContent={renderContent}
      subtitle={
        step === Step.Completed ? (
          ''
        ) : error ? (
          ''
        ) : step === Step.Password ? (
          'Set a new password to secure your Biotandem account.'
        ) : step === Step.OTP ? (
          <>
            Please enter the 6-digit confirmation code sent to <span className="font-bold">{usernameOrEmail}</span>.
          </>
        ) : (
          'Enter your email to receive a 6-digit confirmation code.'
        )
      }
      title={
        step === Step.Completed
          ? ''
          : step === Step.Password
            ? 'Reset Password'
            : step === Step.OTP
              ? 'Enter Confirmation Code'
              : 'Forgot Password?'
      }
    />
  );
  function renderContent() {
    switch (step) {
      case Step.Username:
        return renderContentUsername();
      case Step.OTP:
        return renderContentOTP();
      case Step.Password:
        return renderContentPassword();
      case Step.Completed:
        return renderContentCompleted();
      default:
        return renderContentUsername();
    }
  }
  function renderContentUsername() {
    return (
      <Form
        fields={[
          {
            name: 'usernameOrEmail',
            rules: {
              pattern: {
                value: emailRegExp,
                message: 'Invalid email address. E.g. example@email.com',
              },
              required: 'Email is required.',
            },
            type: 'email',
            typeOptions: {
              autoFocus: true,
              className: 'h-[3rem]',
              placeholder: 'Enter your Biotandem account email',
            },
          },
        ]}
        onSubmit={(fieldValues) =>
          resetPassword(fieldValues)
            .unwrap()
            .then(() => {
              setUsernameOrEmail(fieldValues.usernameOrEmail);
              setStep(Step.OTP);
            })
            .catch((e) => {
              setError(getErrorMessage(e));
              return Promise.reject(e);
            })
        }
        submitButton={{
          className: 'h-[3rem]',
          children: 'Send Code',
        }}
        render={({renderError, renderFieldByName, renderSubmitButton}) => (
          <div className={'flex flex-col gap-4 w-full'}>
            {renderError()}
            <InputGroup position="left" className="[--input-group-height:3rem]">
              <Icon icon="envelope" />
              {renderFieldByName('usernameOrEmail')}
            </InputGroup>
            {renderSubmitButton()}
          </div>
        )}
      />
    );
  }
  function renderContentOTP() {
    return (
      <OTPForm
        cancel={() => setStep(Step.Username)}
        cancelText="Change Email"
        resend={() =>
          resetPasswordResendOTP({
            usernameOrEmail,
          }).unwrap()
        }
        verify={async (otp) => {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          setOTP(otp);
          setStep(Step.Password);
        }}
      />
    );
  }
  function renderContentPassword() {
    return (
      <Form
        fields={[
          {
            name: 'newPassword',
            rules: {
              required: 'New password is required.',
              validate: (value) => validatePassword(value),
            },
            type: 'password',
            typeOptions: {
              autoComplete: 'new-password',
              feedback: false,
              id: 'password',
              inputClassName: 'h-[3rem]',
              onFocus: () => setNewPasswordFocused(true),
              onBlur: () => setNewPasswordFocused(false),
              placeholder: 'Choose a new secure password',
              toggleMask: true,
            },
          },
          {
            name: 'confirmPassword',
            rules: {
              required: 'Confirm password is required.',
              validate: (value, {newPassword}) => {
                if (value !== newPassword) {
                  return 'Your passwords do not match.';
                }
              },
            },
            type: 'password',
            typeOptions: {
              autoComplete: 'new-password',
              feedback: false,
              id: 'confirmPassword',
              inputClassName: 'h-[3rem]',
              placeholder: 'Re-enter your new password',
              toggleMask: true,
            },
          },
        ]}
        id={'reset-password-form'}
        onChange={(fieldValues) => {
          setNewPassword(fieldValues.newPassword ?? '');
        }}
        onSubmit={(fieldValues) =>
          changePassword({
            ...fieldValues,
            usernameOrEmail,
            otp,
          })
            .unwrap()
            .then(() => {
              setStep(Step.Completed);
            })
            .catch((e) => {
              setError(getErrorMessage(e));
              return Promise.reject(e);
            })
        }
        render={({renderError, renderFieldByName, renderSubmitButton}) => {
          return (
            <div className={'flex flex-col gap-4 w-full'}>
              {renderError()}
              <div className="relative">
                {newPasswordFocused && (
                  <div
                    className="absolute bottom-full left-0 right-0 p-4 border border-gray-200 rounded-3xl shadow-md mb-2"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                    }}
                  >
                    <div className="mb-2 font-bold">Password must have</div>
                    <CheckList
                      items={[
                        {
                          checked: newPassword.match(minLengthRegex) != null,
                          label: minLengthMessage,
                        },
                        {
                          checked: newPassword.match(containsNumericRegex) != null,
                          label: containsNumericMessage,
                        },
                        {
                          checked: newPassword.match(containsSpecialCharacterRegex) != null,
                          label: containsSpecialCharacterMessage,
                        },
                      ]}
                    />
                  </div>
                )}
                <InputGroup position="left" className="[--input-group-height:3rem]">
                  <div>{renderFieldByName('newPassword')}</div>
                  <Icon icon="lock" />
                </InputGroup>
              </div>
              <InputGroup position="left" className="[--input-group-height:3rem]">
                <div>{renderFieldByName('confirmPassword')}</div>
                <Icon icon="lock" />
              </InputGroup>
              {renderSubmitButton()}
            </div>
          );
        }}
        submitButton={{
          className: 'h-[3rem]',
          children: 'Reset Password',
        }}
      />
    );
  }
  function renderContentCompleted() {
    return (
      <InfoSection
        actions={[
          {
            props: {
              className: 'w-[10rem]',
              children: 'Sign In',
              onClick: () => navigate('/sign-in'),
            },
            type: 'button',
          },
        ]}
        message={`You can now sign in to Biotandem with your new password.`}
        messageClassName="mt-6"
        icon="check-circle"
        iconClassName="text-green-400 text-8xl"
        title={'Password Reset Successful'}
        titleClassName="text-2xl"
        subtitle={'Your password has been changed.'}
        subtitleClassName="text-muted-foreground"
      />
    );
  }
};
